import React, { useState } from 'react';
import { useBoolean } from '@uifabric/react-hooks';
import { useDispatch } from 'react-redux';
import { Field, Form, Formik } from 'formik';
import {
  ActionButton,
  IconButton,
  Label,
  MessageBar,
  MessageBarType,
  Modal,
  PrimaryButton,
  Spinner,
  SpinnerSize,
  Stack,
  Text,
  DetailsList as FluentDetailList,
  SelectionMode,
  DefaultButton,
} from 'office-ui-fabric-react';
import { useMutation } from 'react-query';
import TextField from '../../components/TextField';
import Module from '../../components/Module';
import ModuleContent from '../../components/ModuleContent';
import SubModule from '../../components/SubModule';
import SubModuleContent from '../../components/SubModuleContent';
import SubModuleHeader from '../../components/SubModuleHeader';
import styles from './DriCacheEviction.style';
import DetailsList from '../../components/DetailsList';
import TextButton from '../../components/TextButton';
import DriOperations from '../../actions/driOperations';
import { showFlashMessage } from '../../actions/ui';
import * as sapi from '../DriContractDebug/services/sapi';

const DriCacheEviction = () => {
  const [cacheKeys, setCacheKeys] = useState([]);
  const [cacheKeyPrefix, setCacheKeyPrefix] = useState('');
  const [isFetchingKeys, setIsFetchingKeys] = useState(false);
  const [hasNoMatchingKeys, setHasNoMatchingKeys] = useState(false);
  const [isModalOpen, { setTrue: showModal, setFalse: hideModal }] = useBoolean(false);
  const [modalContent, setModalContent] = useState('');
  const [isFlushingAll, setIsFlushingAll] = useState(false);
  const [statementDeletingKey, setStatementDeletingKey] = useState(null);
  const dispatch = useDispatch();

  const statementCacheKeys = useMutation('getStatementCacheKeys', () =>
    sapi.get('/dri/statement/cache').then(list => list.map(key => ({ key }))),
  );
  const clearStatementCache = useMutation('clearStatementCache', () =>
    sapi.del('/dri/statement/cache'),
  );
  const deleteStatementCache = useMutation('deleteStatementCache', statementId =>
    sapi.del(`/dri/statement/${statementId}/cache`),
  );

  const initialValues = {
    cacheKeyPrefix,
    cacheKeys,
  };

  const onSearchKeys = async values => {
    setIsFetchingKeys(true);
    const response = await DriOperations.getCacheKeysForPrefix(values.cacheKeyPrefix);
    let responseJson;
    if (response.ok && response.status === 200) {
      responseJson = await response.json();
      if (responseJson && responseJson.length > 0) {
        setCacheKeys(responseJson);
        setHasNoMatchingKeys(false);
      } else {
        setCacheKeys([]);
        setHasNoMatchingKeys(true);
      }
      setCacheKeyPrefix(values.cacheKeyPrefix);
    } else {
      dispatch(
        showFlashMessage('Error while fetching cache keys', {
          type: 'error',
          duration: 10000,
        }),
      );
    }
    setIsFetchingKeys(false);
  };

  const getCacheEntryDetails = async key => {
    const response = await DriOperations.getCacheEntryDetails(key);
    let responseJson;
    if (response.ok && response.status === 200) {
      responseJson = await response.json();
      setModalContent(responseJson);
      showModal();
    } else {
      dispatch(
        showFlashMessage('Error while fetching cache entry details', {
          type: 'error',
          duration: 10000,
        }),
      );
    }
  };

  const deleteCacheEntry = async key => {
    const response = await DriOperations.deleteCacheEntry(key);
    if (response.ok && response.status === 204) {
      // call getKeys
      onSearchKeys({ cacheKeyPrefix });
    } else {
      dispatch(
        showFlashMessage('Error while deleting cache entry', {
          type: 'error',
          duration: 10000,
        }),
      );
    }
  };

  const deleteCacheEntries = async prefix => {
    const response = await DriOperations.deleteCacheEntriesForPrefix(prefix);
    if (response.ok && response.status === 204) {
      // call getKeys
      onSearchKeys({ cacheKeyPrefix });
    } else {
      dispatch(
        showFlashMessage('Error while deleting cache entries', {
          type: 'error',
          duration: 10000,
        }),
      );
    }
  };

  const deleteAllCacheEntries = async () => {
    setIsFlushingAll(true);
    const response = await DriOperations.deleteAllCacheEntries();
    if (response.ok && response.status === 204) {
      dispatch(
        showFlashMessage('Successfully evicted all cache entries.', {
          type: 'success',
          duration: 10000,
        }),
      );
    } else {
      dispatch(
        showFlashMessage('Error while evicting all cache entires', {
          type: 'error',
          duration: 10000,
        }),
      );
    }
    setIsFlushingAll(false);
  };

  const columns = [
    {
      key: 'cacheKey',
      name: 'Cache Key',
      columnActionsMode: 0,
      minWidth: 250,
      maxWidth: 450,
      onRender: cacheKey => <span>{cacheKey}</span>,
    },
    {
      key: 'getDetails',
      name: 'Get Details',
      columnActionsMode: 0,
      minWidth: 100,
      maxWidth: 100,
      onRender: cacheKey => (
        <TextButton type="button" onClick={() => getCacheEntryDetails(cacheKey)} noPadding>
          View value
        </TextButton>
      ),
    },
    {
      key: 'evictKey',
      name: 'Evict Key',
      columnActionsMode: 0,
      minWidth: 100,
      maxWidth: 100,
      onRender: cacheKey => (
        <TextButton
          type="button"
          className={styles.listAction}
          onClick={() => deleteCacheEntry(cacheKey)}
          noPadding
        >
          Evict key
        </TextButton>
      ),
    },
  ];

  const validate = formValues => {
    const errors = {};
    if (formValues.cacheKeyPrefix.length === 0) {
      errors.cacheKeyPrefix = 'Required';
    }

    return errors;
  };

  return (
    <>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validate={validate}
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={onSearchKeys}
      >
        {({ values, submitForm }) => (
          <Form>
            <Module>
              <ModuleContent>
                <SubModule closed>
                  <SubModuleHeader id="searchAndEvictKeysHeader">
                    Search and Evict keys
                  </SubModuleHeader>
                  <SubModuleContent>
                    <Stack horizontal>
                      <Field
                        key="cacheKeyPrefix"
                        name="cacheKeyPrefix"
                        component={TextField}
                        label="Enter the prefix for the cache key(s) to retrieve"
                        placeholder="Cache Key Prefix"
                        className={styles.prefixInputBox}
                        required
                      />
                      <Stack horizontal horizontalAlign="end">
                        <PrimaryButton className={styles.searchKeyBtn} onClick={submitForm}>
                          Search keys
                          {isFetchingKeys && (
                            <>
                              &nbsp;
                              <Spinner size={SpinnerSize.medium} />
                            </>
                          )}
                        </PrimaryButton>
                      </Stack>
                    </Stack>

                    {values.cacheKeys && values.cacheKeys.length > 0 && (
                      <>
                        <div className={styles.resultDiv}>
                          <DetailsList
                            className={styles.cacheKeyList}
                            items={values.cacheKeys}
                            columns={columns}
                          />
                        </div>
                        <div className={styles.deleteAll}>
                          <PrimaryButton onClick={() => deleteCacheEntries(values.cacheKeyPrefix)}>
                            Evict retrieved keys
                          </PrimaryButton>
                        </div>
                        <Modal
                          titleAriaId="title"
                          isOpen={isModalOpen}
                          onDismiss={hideModal}
                          isBlocking={false}
                        >
                          <div className={styles.closeModal}>
                            <IconButton
                              iconProps={{ iconName: 'Cancel' }}
                              title="Cancel"
                              ariaLabel="Cancel"
                              onClick={hideModal}
                            />
                          </div>
                          <div className={styles.cacheValue}>
                            <pre>{JSON.stringify(modalContent, null, 2)}</pre>
                          </div>
                        </Modal>
                      </>
                    )}

                    {hasNoMatchingKeys && (
                      <div className={styles.resultDiv}>
                        <MessageBar
                          messageBarType={MessageBarType.info}
                          messageBarIconProps={{ iconName: 'Info' }}
                        >
                          No cache keys found for the entered prefix.
                        </MessageBar>
                      </div>
                    )}
                  </SubModuleContent>
                </SubModule>
              </ModuleContent>
            </Module>
          </Form>
        )}
      </Formik>

      <Formik>
        <Form>
          <Module>
            <ModuleContent>
              <SubModule closed>
                <SubModuleHeader id="EvictAllKeysHeader">Evict All keys</SubModuleHeader>
                <SubModuleContent>
                  <Stack horizontal>
                    <Label className={styles.flushAllLabel}>Evict all cache entries:</Label>
                    <PrimaryButton
                      className={styles.flushAllButton}
                      onClick={deleteAllCacheEntries}
                    >
                      Evict All
                      {isFlushingAll && <Spinner size={SpinnerSize.medium} />}
                    </PrimaryButton>
                  </Stack>
                </SubModuleContent>
              </SubModule>
            </ModuleContent>
          </Module>
        </Form>
      </Formik>

      <Module>
        <ModuleContent>
          <SubModule closed>
            <SubModuleHeader id="statementCache">Statement Cache</SubModuleHeader>
            <SubModuleContent>
              <Stack tokens={{ childrenGap: 20 }}>
                <Text>Press the button below to load the cache keys</Text>
                <Stack horizontal tokens={{ childrenGap: 30 }}>
                  <PrimaryButton
                    disabled={statementCacheKeys.isLoading}
                    onClick={() => statementCacheKeys.mutate()}
                  >
                    {statementCacheKeys.isLoading ? (
                      <Spinner size={SpinnerSize.xSmall} />
                    ) : (
                      'Show Keys'
                    )}
                  </PrimaryButton>

                  <DefaultButton
                    disabled={clearStatementCache.isLoading}
                    onClick={async () => {
                      await clearStatementCache.mutateAsync();
                      statementCacheKeys.mutate();
                    }}
                  >
                    {statementCacheKeys.isLoading ? (
                      <Spinner size={SpinnerSize.xSmall} />
                    ) : (
                      'Clear Cache'
                    )}
                  </DefaultButton>
                </Stack>

                {statementCacheKeys.isSuccess && statementCacheKeys.data.length === 0 && (
                  <Text>No statements in cache.</Text>
                )}

                {statementCacheKeys.error && (
                  <Stack>
                    {statementCacheKeys.error.response?.data.message ||
                      statementCacheKeys.error.stack}
                  </Stack>
                )}

                {statementCacheKeys.isSuccess && statementCacheKeys.data.length > 0 && (
                  <Stack>
                    <Text>Results</Text>
                    <FluentDetailList
                      items={statementCacheKeys.data}
                      compact
                      selectionMode={SelectionMode.none}
                      columns={[
                        {
                          key: 'key',
                          name: 'Key (StatementId)',
                          fieldName: 'key',
                          minWidth: 200,
                        },
                        {
                          key: 'deleteKey',
                          name: 'Delete Key',
                          minWidth: 200,
                          onRender(item) {
                            return (
                              <ActionButton
                                disabled={deleteStatementCache.isLoading}
                                onClick={async () => {
                                  try {
                                    setStatementDeletingKey(item.key);
                                    await deleteStatementCache.mutateAsync(item.key);
                                    statementCacheKeys.mutate();
                                  } finally {
                                    setStatementDeletingKey(null);
                                  }
                                }}
                              >
                                {deleteStatementCache.isLoading &&
                                statementDeletingKey === item.key ? (
                                  <Spinner size={SpinnerSize.xSmall} />
                                ) : (
                                  'Evict Key'
                                )}
                              </ActionButton>
                            );
                          },
                        },
                      ]}
                    />
                  </Stack>
                )}
              </Stack>
            </SubModuleContent>
          </SubModule>
        </ModuleContent>
      </Module>
    </>
  );
};

export default DriCacheEviction;
